import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import ZeePersonaDataFormSchema from '@/components/forms/schemas/zee-candidate/ZeePersonaDataFormSchema';
import { FormCreatableMultiSelect } from '@/components/global/form-inputs/FormCreatableMultiSelect';
import FormCustomRadioGroup from '@/components/global/form-inputs/FormCustomRadioGroup';
import { FormInput } from '@/components/global/form-inputs/FormInput';
import { FormSelect } from '@/components/global/form-inputs/FormSelect';
import { Form } from '@/components/ui/form';

interface ZeePersonaDataFormProps {
  form: UseFormReturn<z.infer<typeof ZeePersonaDataFormSchema>>;
  dynamicBehaviors?: string[];
  dynamicInterests?: string[];
  dynamicPersonality?: string[];
  onCreateBehavior?: (value: string) => void;
  onCreateInterest?: (value: string) => void;
  onCreatePersonality?: (value: string) => void;
}

const GENDER_OPTIONS = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Non-binary', value: 'non-binary' },
  { label: 'Prefer not to say', value: 'prefer-not-to-say' },
];

const MARITAL_STATUS_OPTIONS = [
  { label: 'Single', value: 'single' },
  { label: 'Married', value: 'married' },
  { label: 'Divorced', value: 'divorced' },
  { label: 'Widowed', value: 'widowed' },
  { label: 'Separated', value: 'separated' },
];

const EDUCATION_OPTIONS = [
  { label: 'High School', value: 'high-school' },
  { label: 'Some College', value: 'some-college' },
  { label: 'Associate Degree', value: 'associate' },
  { label: 'Bachelor Degree', value: 'bachelor' },
  { label: 'Master Degree', value: 'master' },
  { label: 'Doctorate', value: 'doctorate' },
  { label: 'Professional Degree', value: 'professional' },
];

const CHILDREN_AGE_RANGES = [
  { label: '0-3 years', value: '0-3' },
  { label: '4-6 years', value: '4-6' },
  { label: '7-12 years', value: '7-12' },
  { label: '13-17 years', value: '13-17' },
  { label: '18+ years', value: '18+' },
];

const RENT_OR_OWN_OPTIONS = [
  { label: 'Renter', value: 'renter' },
  { label: 'Home Owner', value: 'home-owner' },
];

export const ZeePersonaDataForm = ({
  form,
  dynamicBehaviors = [],
  dynamicInterests = [],
  dynamicPersonality = [],
  onCreateBehavior,
  onCreateInterest,
  onCreatePersonality,
}: ZeePersonaDataFormProps) => {
  const behaviorsOptions = dynamicBehaviors.map((value) => ({
    value,
    label: value,
  }));

  const interestsOptions = dynamicInterests.map((value) => ({
    value,
    label: value,
  }));

  const personalityOptions = dynamicPersonality.map((value) => ({
    value,
    label: value,
  }));

  return (
    <Form {...form}>
      <form className="w-full space-y-1.5">
        <div className="flex flex-col gap-6 rounded-xl border border-border bg-white p-6">
          <div className="flex flex-col gap-2">
            <p className="as-title-03 as-strong w-[280px] font-medium text-zinc-700">
              Demographics
            </p>
            <div className="h-px bg-border" />
          </div>
          <div className="grid flex-1 grid-cols-2 gap-6">
            <FormInput
              name="age"
              label="Age"
              type="number"
              control={form.control}
            />
            <FormSelect
              name="gender"
              label="Gender"
              control={form.control}
              options={GENDER_OPTIONS}
              placeholder="Select gender"
            />
            <FormSelect
              name="marital_status"
              label="Marital Status"
              control={form.control}
              options={MARITAL_STATUS_OPTIONS}
              placeholder="Select marital status"
            />
            <FormSelect
              name="education"
              label="Education Level"
              control={form.control}
              options={EDUCATION_OPTIONS}
              placeholder="Select education level"
            />
            <FormCustomRadioGroup
              name="rentorhomeowner"
              label="Rent or Own"
              control={form.control}
              options={RENT_OR_OWN_OPTIONS}
            />
          </div>
        </div>

        <div className="flex flex-col gap-6 rounded-xl border border-border bg-white p-6">
          <div className="flex flex-col gap-2">
            <p className="as-title-03 as-strong w-[280px] font-medium text-zinc-700">
              Children & Family Segmentation
            </p>
            <div className="h-px bg-border" />
          </div>
          <div className="grid flex-1 grid-cols-2 gap-6">
            <FormInput
              name="children_between_ages_0_3"
              label="Number of Children (0-3 years)"
              type="number"
              control={form.control}
            />
            <FormCreatableMultiSelect
              name="children_age_ranges"
              label="Children Age Ranges"
              control={form.control}
              options={CHILDREN_AGE_RANGES}
              placeholder="Select age ranges"
            />
          </div>
        </div>

        <div className="flex flex-col gap-6 rounded-xl border border-border bg-white p-6">
          <div className="flex flex-col gap-2">
            <p className="as-title-03 as-strong w-[280px] font-medium text-zinc-700">
              Lifestyle & Behavioral Attributes
            </p>
            <div className="h-px bg-border" />
          </div>
          <div className="grid flex-1 grid-cols-1 gap-6">
            <FormCreatableMultiSelect
              name="behaviors"
              label="Behaviors"
              control={form.control}
              options={behaviorsOptions}
              placeholder="Add behaviors (e.g., Healthy living, Luxury lifestyle)"
              onCreateOption={onCreateBehavior}
            />
            <FormCreatableMultiSelect
              name="interests"
              label="Interests"
              control={form.control}
              options={interestsOptions}
              placeholder="Add interests (e.g., Magazine Subscriber, Pet owner)"
              onCreateOption={onCreateInterest}
            />
            <FormCreatableMultiSelect
              name="personality"
              label="Personality Traits"
              control={form.control}
              options={personalityOptions}
              placeholder="Add personality traits (e.g., ambitious, creative)"
              onCreateOption={onCreatePersonality}
            />
          </div>
        </div>
      </form>
    </Form>
  );
};
